import { useEffect, useState } from 'react';
import { ArrowLeft, Download, FileText, Folder, Image as ImageIcon } from 'lucide-react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { loadMiscItems } from '../lib/library';
import { miscFileKind } from '../lib/library';
import type { MiscCategory, MiscItem } from '../lib/library';
import { queryClass } from '../lib/parse';
import type { ParseRecord } from '../lib/parse';
import { ErrorState, LoadingState } from '../components/ui';

export function StudentLibraryFolder() {
  const { categoryId = '' } = useParams();
  const [searchParams] = useSearchParams();
  const folderId = searchParams.get('folder') ?? undefined;
  const [category, setCategory] = useState<MiscCategory>();
  const [items, setItems] = useState<MiscItem[]>();
  const [failed, setFailed] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    setLoading(true);
    void (async () => {
      try {
        const [categoryRows, rows] = await Promise.all([
          folderId ? Promise.resolve(undefined) : queryClass('elearning_MiscellCategories', { limit: 1, where: (query) => query.equalTo('objectId', categoryId) }) as Promise<ParseRecord[]>,
          loadMiscItems(categoryId, folderId),
        ]);
        if (!active) return;
        setCategory(categoryRows?.[0] as MiscCategory | undefined);
        setItems(rows);
        setFailed(false);
      } catch {
        if (active) setFailed(true);
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [categoryId, folderId]);

  const title = category?.CatName || 'Library';
  const backTo = folderId ? `/library/${categoryId}` : '/library';
  return <div className="library-page">
    <Link className="back-link" to={backTo}><ArrowLeft size={17} /> {folderId ? title : 'Library'}</Link>
    <section className="utility-hero"><div className="utility-icon"><Folder size={26} /></div><div><h1>{title}</h1><p>{category?.CatDesc || 'Browse study materials'}</p></div></section>
    {loading ? <LoadingState /> : failed ? <ErrorState onRetry={() => window.location.reload()} />
      : items?.length
        ? <div className="subject-list">{items.map((item) => <MiscItemRow key={item.objectId} item={item} categoryId={categoryId} />)}</div>
        : <p className="detail-description">This collection is empty.</p>}
  </div>;
}

function MiscItemRow({ item, categoryId }: { item: MiscItem; categoryId: string }) {
  const kind = miscFileKind(item);
  const Icon = kind === 'pdf' ? FileText : kind === 'image' ? ImageIcon : kind === 'other' ? Download : Folder;
  const meta = item.Description || (kind === 'pdf' ? 'PDF document' : kind === 'image' ? 'Image' : kind === 'other' ? 'Open file' : 'Folder');
  const href = kind === 'folder' ? `/library/${categoryId}?folder=${item.objectId}`
    : kind === 'other' ? item.Link || '#'
      : `/library/item/${item.objectId}`;
  const openInNewTab = kind === 'other';
  return <Link className="subject-row material-row" key={item.objectId} to={href} {...openInNewTab ? { target: '_blank', rel: 'noreferrer' } : {}}>
    <span className="material-icon"><Icon size={17} /></span>
    <div><strong>{item.Name || 'Untitled file'}</strong><small>{meta}{item.MaterialPaidType === 'Paid' ? ' · Paid' : ''}</small></div>
    {kind === 'other' && <Download size={15} />}
  </Link>;
}
